import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      { path: '', component: () => import('@/pages/IndexPage.vue'), meta: { requiresAuth: true, title: 'Home' } },
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/pages/LoginPage.vue'),
        meta: { requiresAuth: true, title: 'Login' },
      },
      {
        path: 'edit-locations:id?',
        name: 'EditLocations',
        component: () => import('@/pages/EditLocationsPage.vue'),
        meta: { requiresAuth: true, title: 'Location Editor' },
      },
      {
        path: 'business-list',
        name: 'BusinessList',
        component: () => import('@/pages/BusinessListPage.vue'),
        meta: { requiresAuth: true, title: 'Business List', keepAlive: true },
      },
      {
        path: 'damage-reports',
        name: 'DamageReports',
        component: () => import('@/pages/DamageReportsPage.vue'),
        meta: { requiresAuth: true, title: 'Damage Reports' },
      },
      {
        path: 'workorders',
        name: 'Workorders',
        component: () => import('@/pages/WorkOrdersPage.vue'),
        meta: { requiresAuth: true, title: 'Work Orders' },
      },
      {
        path: 'scheduledstops',
        name: 'ScheduledStops',
        component: () => import('@/pages/ScheduledStopsPage.vue'),
        meta: { requiresAuth: true, title: 'Scheduled Stops' },
      },
      {
        path: 'revenue-overview',
        name: 'RevenueOverview',
        component: () => import('@/pages/RevenueOverviewPage.vue'),
        meta: { requiresAuth: true, title: 'Revenue Overview' },
      },
      {
        path: 'revenue-breakdown',
        name: 'RevenueBreakdown',
        component: () => import('@/pages/RevenueBreakdownPage.vue'),
        meta: { requiresAuth: true, title: 'Revenue Breakdown' },
      },
      {
        path: 'payment-workbench',
        name: 'PaymentWorkbench',
        component: () => import('@/pages/PaymentWorkbenchPage.vue'),
        meta: { requiresAuth: true, title: 'Payment Workbench' },
      },
      {
        path: 'invoicing-contracts',
        name: 'InvoicingContracts',
        component: () => import('@/pages/InvoicingContractsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Invoicing Contracts',
        },
      },
      {
        path: 'product-commercial-profiles',
        name: 'ProductCommercialProfiles',
        component: () => import('@/pages/ProductPricingTiersPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Product Commercial Profiles',
        },
      },
      {
        path: 'invoicing-contracts/:id',
        name: 'InvoicingContractDetail',
        component: () => import('@/pages/ContractDetailPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Contract',
        },
      },
      {
        path: 'billing-runs',
        name: 'BillingRuns',
        component: () => import('@/pages/BillingRunsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Billing Runs',
        },
      },
      {
        path: 'billing-runs/:id',
        name: 'BillingRunDetail',
        component: () => import('@/pages/BillingRunDetailPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Billing Run Detail',
        },
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/pages/InvoicesPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Invoices',
          requiresFinance: true,
        },
      },
      {
        path: 'invoices/:id',
        name: 'InvoiceDetail',
        component: () => import('@/pages/InvoiceDetailPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Invoice Detail',
          requiresFinance: true,
        },
      },
      {
        path: 'map-planner',
        name: 'MapPlanner',
        component: () => import('@/pages/MapPlannerPage.vue'),
        meta: { requiresAuth: true, title: 'Map Planner' },
      },
      {
        path: 'location-stats',
        name: 'LocationStats',
        component: () => import('@/pages/LocationStatsPage.vue'),
        meta: { requiresAuth: true, title: 'Location Stats' },
      },
      {
        path: 'weeklyplanner',
        name: 'WeeklyPlanner',
        component: () => import('@/pages/WeeklyPlannerPage.vue'),
        meta: { requiresAuth: true, title: 'Weekly Planner' },
      },
      {
        path: 'today',
        name: 'Today',
        component: () => import('@/pages/TodayPage.vue'),
        meta: { requiresAuth: true, title: 'Today' },
      },
      {
        path: 'bikes/:businessId?',
        name: 'Bikes',
        component: () => import('@/pages/BikesPage.vue'),
        meta: { requiresAuth: true, title: 'Bikes' },
      },
      {
        path: 'bookings',
        name: 'Bookings',
        component: () => import('@/pages/BookingsPage.vue'),
        meta: { requiresAuth: true, title: 'Bookings' },
      },
      {
        path: 'bike-components',
        name: 'BikeComponents',
        component: () => import('@/pages/BikeComponentsPage.vue'),
        meta: { requiresAuth: true, title: 'Bike Components' },
      },
      {
        path: 'business-editor/:id',
        name: 'BusinessEditor',
        component: () => import('@/pages/BusinessEditorPage.vue'),
        meta: { requiresAuth: true, title: 'Business Editor' },
      },
      {
        path: 'warehouses',
        name: 'Warehouses',
        component: () => import('@/pages/WarehouseManagementPage.vue'),
        meta: { requiresAuth: true, title: 'Warehouses' },
      },
      {
        path: 'parts-orders/to-order',
        name: 'PartsOrdersToOrder',
        component: () => import('@/pages/PartsOrderToOrderPage.vue'),
        meta: { requiresAuth: true, title: 'To order' },
      },
      {
        path: 'parts-orders',
        name: 'PartsOrders',
        component: () => import('@/pages/PartsOrdersPage.vue'),
        meta: { requiresAuth: true, title: 'Parts orders' },
      },
      {
        path: 'parts-orders/:id',
        name: 'PartsOrderDetails',
        component: () => import('@/pages/PartsOrderDetailsPage.vue'),
        meta: { requiresAuth: true, title: 'Parts order' },
      },
      {
        path: 'stock-requests',
        name: 'Stock Requests',
        component: () => import('@/pages/StockRequestsPage.vue'),
        meta: { requiresAuth: true, title: 'Stock Requests' },
      },
      {
        path: 'stock-levels',
        name: 'StockLevels',
        component: () => import('@/pages/WarehousesPage.vue'),
        meta: { requiresAuth: true, title: 'Stock Levels' },
      },
      {
        path: 'products',
        name: 'Products',
        component: () => import('@/pages/ProductsPage.vue'),
        meta: { requiresAuth: true, title: 'Products' },
      },
      {
        path: 'stock-counts',
        name: 'StockCounts',
        component: () => import('@/pages/StockCountsPage.vue'),
        meta: { requiresAuth: true, title: 'Stock Counts' },
      },
      {
        path: 'stock-counts/:id',
        name: 'StockCountDetails',
        component: () => import('@/pages/StockCountDetailsPage.vue'),
        meta: { requiresAuth: true, title: 'Stock Count Details' },
      },
      {
        path: 'missing-spare-parts',
        name: 'Missing Spare Parts',
        component: () => import('@/pages/MissingSparePartsPage.vue'),
        meta: { requiresAuth: true, title: 'Missing Spare Parts' },
      },
      {
        path: 'warranty-cases',
        name: 'Warranty Cases',
        component: () => import('@/pages/WarrantyCasesPage.vue'),
        meta: { requiresAuth: true, title: 'Warranty Cases' },
      },
      {
        path: 'make-and-models',
        name: 'MakeAndModels',
        component: () => import('@/pages/MakeAndModelPage.vue'),
        meta: { requiresAuth: true, title: 'Make and Models' },
      },
      {
        path: 'task-types',
        name: 'TaskTypes',
        component: () => import('@/pages/TaskInspectionTypesPage.vue'),
        meta: { requiresAuth: true, title: 'Task Types' },
      },
      {
        path: 'inspection-types',
        name: 'InspectionTypes',
        component: () => import('@/pages/InspectionTypesPage.vue'),
        meta: { requiresAuth: true, title: 'Inspection Types' },
      },
      {
        path: 'checklists',
        name: 'Checklists',
        component: () => import('@/pages/ChecklistsPage.vue'),
        meta: { requiresAuth: true, title: 'Checklists' },
      },
      {
        path: 'uptime',
        name: 'Uptime',
        component: () => import('@/pages/UptimePage.vue'),
        meta: { requiresAuth: true, title: 'Uptime' },
      },
      {
        path: 'marketing',
        name: 'Marketing',
        component: () => import('@/pages/MarketingPage.vue'),
        meta: { requiresAuth: true, title: 'Marketing' },
      },
      {
        path: 'sales-forecasting',
        name: 'SalesForecasting',
        component: () => import('@/pages/SalesForecastingPage.vue'),
        meta: { requiresAuth: true, title: 'Sales Forecasting' },
      },
      {
        path: 'stop-analysis',
        name: 'StopAnalysis',
        component: () => import('@/pages/StopAnalysisPage.vue'),
        meta: { requiresAuth: true, title: 'Stop Analysis' },
      },
    ],
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('@/pages/ErrorNotFound.vue'),
  },
]

export default routes
